'use client';
import { useTranslations } from 'next-intl';
import clsx from 'clsx';
import { Button } from '@nf/ui';
import { SearchInput } from './search-input';
import { searchService } from '@/services/search.service';
import { useEffect, useState } from 'react';
import { fetchGetSearchResult } from '@/hooks/query/use-search-data';
import { useAccountInfo } from '@/hooks/query/account';
import { SearchResult } from './types';
import { useQueryClient } from '@tanstack/react-query';
import queryKeys from '@/constants/query-keys';
import { useSiteStore } from '@/store/site';
import { useFilterSabaString } from '@/hooks/query/use-saba-filter';
import { getAuthToken, useAuthToken } from '@/store/auth';
import { useAtomValue } from 'jotai';
import { visitorAtom } from '@/atoms/visitor';
import { searchStyle } from '../search-component.style';
import { getForbiddenSport } from '@/services/settings.service';
import { useAppearanceStore } from '@/store/appearance';
import { useAnalyseAttribute } from '@/features/next-analyse';

interface SearchBarProps {
	autoCompleteDatas ?: Array<string>;
    setAutoCompleteResults : (value: Array<string>) => void;
	setSearchResults: (value: Array<SearchResult>) => void;
    autoCompleteResults : Array<string>;
}
export const SearchBar = ({
	autoCompleteDatas,
	setAutoCompleteResults: setAutoCompleteResults,
	setSearchResults: setSearchResults,
	autoCompleteResults: autoCompleteResults
}: SearchBarProps) => {
	const [isOpened, setIsOpened] = useState(false);
	const t = useTranslations('General');
	const taggedElement = useAnalyseAttribute('cancel-search');

	useEffect(() => {
		if (autoCompleteResults.length >= 0 && searchService.searchInputValue !== '') {
			setIsOpened(true);
		} else {
			setIsOpened(false);
		}
	}, [autoCompleteResults])
	return (
		<div className={clsx('search__bar', searchStyle.searchBar)} data-active={isOpened}>
			<SearchInput 
				autoCompleteDatas={autoCompleteDatas}
				setAutoCompleteResults={setAutoCompleteResults}
			/>
			{/* Mars 專用，可關掉 Search 彈窗 */}
			<Button
				variant="classic"
				ref={taggedElement}
				className={searchStyle.cancelButton}
				onClick={() => searchService.displaySearchModal(false)}
			>
				{t('lbl_cancel')}
			</Button>
			<SearchItems 
				autoCompleteResults={autoCompleteResults} 
				setSearchResults={setSearchResults}
				setIsOpened={setIsOpened}
			/>
		</div>
	)
}
interface SearchItemsProps {
    autoCompleteResults ?: Array<string>;
	setSearchResults: (value: Array<SearchResult>) => void;
	setIsOpened : (value: boolean) => void;
}
const SearchItems = ({ autoCompleteResults, setIsOpened, setSearchResults }: SearchItemsProps) => {
	const taggedElement = useAnalyseAttribute('switch-to-search-result-via-keywords-list');
	const t = useTranslations('General');
	autoCompleteResults = autoCompleteResults ?? [];
	return (
		<div 
			className={clsx('search__filters', searchStyle.filter)}
			ref={taggedElement}
		>
			{autoCompleteResults.length > 0 ? autoCompleteResults.map((autoCompleteResult, index) => (
				<SearchItem 
					key={index}
					autoCompleteResult={autoCompleteResult}
					setIsOpened={setIsOpened}
					setSearchResults={setSearchResults}
				/>
			)
			) : (<div className="search__filter search__filter--null"><span className="search__filter-text">{t('lbl_NoResult')}</span></div>)}
		</div>
        
	)
}
interface SearchItemProps {
    autoCompleteResult : string;
	setIsOpened : (value: boolean) => void;
	setSearchResults: (value: Array<SearchResult>) => void;
}
const SearchItem = ({ autoCompleteResult, setIsOpened, setSearchResults }: SearchItemProps) => {
	const [isHover, setIsHover] = useState(false);
	const { user } = useAccountInfo();
	const queryClient = useQueryClient();
	const handleMouseOver = () => {
		setIsHover(true);
	};
	const { filterSabaString } = useFilterSabaString();
	const apiDomain = useSiteStore(state => state.apiDomain);
	const handleMouseOut = () => {
		setIsHover(false);
	};
	
	const isBefore = !Boolean(useAuthToken());
	const visitorInfo = useAtomValue(visitorAtom);
	const useToken = isBefore ? visitorInfo?.AccessToken : getAuthToken();
	const view = useAppearanceStore(state => state.view);
	const handleClickResult = (result: string) => {
		fetchGetSearchResult(apiDomain, { searchinput: result, userlanguage: user?.Lang ?? 'en-US' }, useToken).then(res => {
			if (res.data) {
				const searchResults = res.data.filter(searchResult => !getForbiddenSport(view).includes(searchResult.SportType?.toString() || ''));
				setSearchResults(searchResults);
			}
			queryClient.invalidateQueries({
				queryKey: [queryKeys.SEARCH_HISTORY],
			})
		});
		setIsOpened(false);
		searchService.setSearchInput(result);
	}

	const hightLightSearchInputValueInAutoCompleteResult = (text: string) => {
		const searchInputValue = searchService.searchInputValue;
		if (searchInputValue.length > 0) {
			const tempInput = searchInputValue.replace(/([.?*+^$[\]\\(){}|-])/g, '\\$1');
			const check = new RegExp(tempInput, 'ig');			
			text = text.replace(check, (match) => {
				return `<span class="search__filter-highlight">${match}</span>`;
			});
		}
		return text
	}
	
	return (
		<div className={`search__filter ${isHover ? 'search__filter--selected' : ''}`} 
			onMouseOver={handleMouseOver} 
			onMouseOut={handleMouseOut}
			onClick={() => handleClickResult(filterSabaString(autoCompleteResult))}
		>
			<span className={clsx('search__filter-text', searchStyle.searchText)} dangerouslySetInnerHTML={{ __html: (hightLightSearchInputValueInAutoCompleteResult(filterSabaString(autoCompleteResult))) }}>
			</span>
		</div>
	)
}